import type { PolicyDocument } from './types';

const EFFECTIVE = '1 June 2025';
const UPDATED = '8 June 2026';
const CONTACT = 'support@gograbjob.com';

export const cookiePolicy: PolicyDocument = {
  id: 'cookie',
  title: 'Cookie Policy',
  eyebrow: 'Legal · Cookies',
  effectiveDate: EFFECTIVE,
  lastUpdated: UPDATED,
  summary:
    'This policy explains which cookies and similar technologies GoGrabJob uses, why we use them, and how you can control non-essential tracking through our consent banner or browser settings.',
  sections: [
    {
      title: '1. What are cookies?',
      paragraphs: [
        'Cookies are small text files stored on your device when you visit a website. They help sites remember your session, preferences, and — with your consent — understand how the product is used.',
        'GoGrabJob also uses browser local storage for some preferences (e.g. theme and language). Where this policy refers to "cookies", similar storage technologies are included unless we say otherwise.',
      ],
    },
    {
      title: '2. How we use consent',
      paragraphs: [
        'When you first visit GoGrabJob, a cookie banner lets you Accept all, Opt out of non-essential cookies, or manage categories individually. Your choice is stored for up to one year, after which we may ask again.',
        'Essential cookies cannot be disabled because the site will not function without them (sign-in, security, consent record).',
      ],
      callout:
        'You can reopen cookie settings anytime from the footer link "Cookie settings" (where available) or by clearing site data and revisiting the site.',
    },
    {
      title: '3. Cookie categories',
      subsections: [
        {
          title: '3.1 Strictly necessary',
          paragraphs: [
            'Required for authentication, security, and remembering your consent choice. These do not require consent under most privacy laws.',
          ],
        },
        {
          title: '3.2 Functional',
          paragraphs: [
            'Remember choices that improve your experience — language, theme, layout, and UI preferences. Disabled if you opt out of functional cookies.',
          ],
        },
        {
          title: '3.3 Analytics',
          paragraphs: [
            'Help us understand traffic patterns, feature usage, and errors so we can improve GoGrabJob. Loaded only after you consent to analytics.',
          ],
        },
        {
          title: '3.4 Marketing',
          paragraphs: [
            'Used to measure ad campaigns and attribute sign-ups (e.g. Meta Pixel). Loaded only after you consent to marketing cookies.',
          ],
        },
      ],
    },
    {
      title: '4. Cookies we set',
      table: {
        headers: ['Name / key', 'Category', 'Provider', 'Purpose', 'Duration'],
        rows: [
          ['sb-*-auth-token', 'Necessary', 'Supabase', 'Keeps you signed in securely', 'Session / refresh'],
          ['cookie-consent-preferences', 'Necessary', 'GoGrabJob', 'Stores your cookie choices', '1 year'],
          ['app-theme', 'Functional', 'GoGrabJob', 'Light / dark mode preference (local storage)', 'Until cleared'],
          ['i18nextLng', 'Functional', 'GoGrabJob', 'Language preference', 'Persistent'],
          ['user-preferences', 'Functional', 'GoGrabJob', 'Dashboard and UI settings', '1 year'],
          ['recently-viewed', 'Functional', 'GoGrabJob', 'Recently viewed items in workspace', '30 days'],
          ['_ga', 'Analytics', 'Google Analytics', 'Distinguishes users', '2 years'],
          ['_gid', 'Analytics', 'Google Analytics', 'Distinguishes users', '24 hours'],
          ['_clck', 'Analytics', 'Microsoft Clarity', 'Persists Clarity user ID', '1 year'],
          ['_clsk', 'Analytics', 'Microsoft Clarity', 'Session replay connection', '1 day'],
          ['_fbp', 'Marketing', 'Meta (Facebook)', 'Ad delivery and conversion measurement', '3 months'],
          ['_gcl_au', 'Marketing', 'Google', 'Ad conversion linker', '3 months'],
        ],
      },
      callout:
        'Third-party names may change when providers update their services. We review this table when we add or remove trackers.',
    },
    {
      title: '5. Third-party services',
      list: [
        'Supabase — authentication session cookies.',
        'Google Analytics — aggregated usage statistics (consent required).',
        'Microsoft Clarity — session recordings and heatmaps (consent required).',
        'Meta Pixel — marketing attribution (consent required).',
        'Cashfree / Razorpay — may set cookies during checkout on their domains.',
      ],
      paragraphs: [
        'Third parties process data under their own policies when you interact with their scripts. We load non-essential scripts only after consent.',
      ],
    },
    {
      title: '6. Managing cookies',
      subsections: [
        {
          title: '6.1 GoGrabJob consent banner',
          list: [
            'Click Opt out on the banner to refuse non-essential cookies.',
            'Use Manage preferences (where shown) to toggle categories individually.',
            'Accept all enables functional, analytics, and marketing cookies.',
          ],
        },
        {
          title: '6.2 Browser controls',
          paragraphs: [
            'Most browsers let you block or delete cookies. Blocking all cookies may prevent sign-in or break core features. Consult your browser help pages for instructions.',
          ],
        },
        {
          title: '6.3 Do Not Track',
          paragraphs: [
            'We honour your in-product consent choices. Browser DNT signals are not universally standardised; our consent banner is the primary control for GoGrabJob.',
          ],
        },
      ],
    },
    {
      title: '7. Regional rights',
      subsections: [
        {
          title: '7.1 GDPR (EEA / UK)',
          list: [
            'Non-essential cookies require consent before placement.',
            'You may withdraw consent at any time via cookie settings.',
            'See our GDPR Compliance page for full data subject rights.',
          ],
        },
        {
          title: '7.2 DPDP Act (India)',
          list: [
            'We provide clear notice before using non-essential cookies.',
            'Consent can be withdrawn as easily as it was given.',
            'See our DPDP Compliance page for India-specific rights.',
          ],
        },
      ],
    },
    {
      title: '8. Updates',
      paragraphs: [
        'We may update this Cookie Policy when we add tools, change providers, or laws require it. The "Last updated" date at the top will change. Material changes may be highlighted in the consent banner.',
      ],
    },
    {
      title: '9. Contact',
      paragraphs: [
        `Cookie or privacy questions: ${CONTACT}`,
        'See also: Privacy Policy, GDPR Compliance, DPDP Compliance.',
      ],
    },
  ],
};
